/**
 * A snapshot of a report: its content kept at a moment, to go back to and to
 * compare with.
 *
 * What the list shows of one; its content is fetched when somebody opens it. A
 * snapshot is never changed once taken (only its name and comment, and not even
 * those once the report was validated), so everything here is read-only.
 */

export interface SnapshotSummary {
  readonly id: number
  readonly draftId: number
  /** Counted from 1 for the report: what people call V1, V2. */
  readonly number: number
  /** `manual` (somebody asked) or `auto` (taken by the application). */
  readonly kind: 'manual' | 'auto'
  /** Why an automatic one was taken, as a code: `state:valide`, `restore:V3`. */
  readonly reason: string
  readonly label: string
  readonly comment: string
  /** The workflow state of the report when it was taken. */
  readonly state: string
  /** From the validation on: it can be neither renamed nor deleted. */
  readonly locked: boolean
  readonly author: string
  /** A hash of the content: the same content always gives the same one. */
  readonly digest: string
  readonly sizeBytes: number
  readonly createdAt: string
}

/** The code behind an automatic snapshot, taken apart. */
export type SnapshotReason =
  | { readonly kind: 'state'; readonly state: string }
  | { readonly kind: 'restore'; readonly from: string }
  | { readonly kind: 'other'; readonly code: string }

export function readReason(reason: string): SnapshotReason {
  const [head = '', ...rest] = reason.split(':')
  const tail = rest.join(':')
  if (head === 'state' && tail) return { kind: 'state', state: tail }
  if (head === 'restore' && tail) return { kind: 'restore', from: tail }
  return { kind: 'other', code: reason }
}

/** The stored shape of a snapshot, as the API sends it. */
export interface StoredSnapshot {
  id: number
  draft_id: number
  number: number
  kind: string
  reason: string
  label: string
  comment: string
  state: string
  locked: boolean
  author: string
  digest: string
  size_bytes: number
  created_at: string
}

export function readSnapshot(stored: StoredSnapshot): SnapshotSummary {
  return {
    id: stored.id,
    draftId: stored.draft_id,
    number: stored.number,
    kind: stored.kind === 'auto' ? 'auto' : 'manual',
    reason: stored.reason,
    label: stored.label,
    comment: stored.comment,
    state: stored.state,
    locked: stored.locked,
    author: stored.author,
    digest: stored.digest,
    sizeBytes: stored.size_bytes,
    createdAt: stored.created_at,
  }
}
